/**
 * audio-overlap.js — window.AudioOverlap / module.exports
 *
 * Две части ОДНОЙ записи, которые повторяют друг друга на стыке: где именно
 * начало B повторяет конец A — и куда поставить стык, чтобы шва не было.
 *
 * ── Откуда задача ──────────────────────────────────────────────────────────
 * Обращение 29.09.2026 с /ru/join: программа записи бесплатно пишет 3 минуты,
 * поэтому песня приходит двумя кусками, и второй человек начинает писать за
 * несколько секунд до конца первого. Дальше — ручной поиск стыка в /trim:
 * 16 минут на первую песню, 6 — на вторую (телеметрия сессий 29–30.09).
 *
 * ── Почему это самый лёгкий случай выравнивания — и где его подвох ────────
 * В нахлёсте один и тот же цифровой сигнал (оба куска записаны с одного и
 * того же воспроизведения), разница только в шуме кодека и в дробном сдвиге
 * сетки отсчётов. Поэтому совпадение ищется по ФОРМЕ ВОЛНЫ, а не по
 * огибающей, как в AudioAlign / AudioSyncOffset (там сигналы разные: голос
 * против минуса, камера против рекордера).
 *
 * ⚠️ Подвох — повторы в самой музыке. Припев, куплет на той же гармонии,
 * луп в электронике: начало B похоже на НЕСКОЛЬКО мест в A. Поэтому
 * гипотеза «B начинается в точке t записи A» проверяется на ВСЁМ нахлёсте
 * сразу — хвост A от t до конца обязан совпасть с головой B той же длины.
 * Ложный припев на этом рвётся: после него у A идёт одно, у B — другое.
 *
 * ── Устройство ────────────────────────────────────────────────────────────
 *   edges(channels, sampleRate)  — при разборе дорожки: моно, ФНЧ, прореживание
 *     до ~8 кГц, первые и последние EDGE_SEC секунд в Int16. Буфер дорожки
 *     /join держит не всегда (вытеснение по бюджету памяти), края — всегда.
 *   match(edgesA, edgesB)        — по краям двух дорожек:
 *     1) грубо: взаимная корреляция через БПФ на ~1 кГц, нормированная на
 *        энергии ВСЕГО нахлёста при каждом сдвиге;
 *     2) точно: до отсчёта ~8 кГц около лучших кандидатов + парабола;
 *     3) проверка: корреляция по всему нахлёсту — порог MATCH_CORR;
 *     4) стык: в середине нахлёста, в самом тихом согласном окне.
 *   Честные отказы: 'same' — один и тот же файл дважды (его кладут, чтобы
 *   зациклить); 'ambiguous' — порог проходят ДВА разных стыка (побитный луп:
 *   сколько кругов было в песне, из записей не узнать).
 *
 * ── Что замерено (01.10.2026) ────────────────────────────────────────────
 * Прибор tests/js/audio-overlap-bench.mjs: все 34 песни public/audio/ai-styles,
 * 238 случаев. Части как у рекордера с лимитом — каждая своим MP3-кодированием
 * (192 кбит/с на 44.1 кГц со своей сеткой отсчётов; на 48 кГц с B тише на
 * 2 дБ; 128 кбит/с), нахлёст 1.5–30 с; отрицательные — разрыв 0.3/2/30 с,
 * чужая песня, обратный порядок.
 *   истинные пары   97 из 102 найдены (пять промахов — нахлёст 51 с, больше
 *                   краёв EDGE_SEC); корреляция 0.950–0.999, медиана 0.994;
 *                   ошибка точки стыка ≤ 8 мкс; длина склейки = длине песни
 *                   ±1 мс; у стыка ошибка не выше, чем у каждой части сама
 *                   по себе; −2 дБ названы как 1.89–1.99
 *   отрицательные   136 случаев, ни одного ложного; максимум 0.757 (разрыв
 *                   0.3 с в одной гармонии) — отсюда MATCH_CORR 0.9
 *   второй кандидат у истинных пар — не выше 0.749 (повторы не побитные)
 *   скорость (Node, без нагрузки) края 35 мс на дорожку, сверка пары 28 мс
 * В браузере (tests/playwright/join-overlap-seam.mjs, части 3:00 и с 2:51.5,
 * поданы в обратном порядке): вторая часть в скачанном файле стоит там же,
 * где в песне, — 0.00 отсчёта в MP3 (Web Audio) и 0.01 во FLAC (FFmpeg).
 *
 * ── Отвергнуто ────────────────────────────────────────────────────────────
 *   · огибающая (как в AudioAlign) — 10 мс вместо отсчёта, а сигнал тут один;
 *   · проверка по окну в начале нахлёста — ложный припев проходит её наравне
 *     с настоящим стыком (узловой тест держит это мутантом);
 *   · выбор «наугад» между двумя равноправными стыками — прибавил бы или
 *     отнял круг лупа; отсюда 'ambiguous'.
 * На стороне /join: обрезки на ЦЕЛЫХ отсчётах (дробная — интерполяция всей
 * второй части, смазанные верха на ударах), переход ЛИНЕЙНЫЙ (равномощный
 * даёт +3 дБ на одинаковых сигналах), atrim в FFmpeg-пути — в микросекундах.
 *
 * Чистая математика: ни DOM, ни Web Audio. Те же функции гоняются в Node на
 * настоящих песнях (tests/js/audio-overlap.test.mjs).
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.AudioOverlap = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* Сколько секунд с каждого конца дорожки смотрим. Это и потолок нахлёста. */
    var EDGE_SEC = 45;
    /* Частота краёв. Делитель целый (48 кГц → 8000, 44.1 кГц → 7350): дробный
       шаг прореживания дал бы краям двух дорожек разную сетку времени. */
    var EDGE_RATE = 8000;
    /* Грубый поиск идёт на частоте краёв / COARSE_DIV (~1 кГц). */
    var COARSE_DIV = 8;
    /* Короче — уже не нахлёст, а совпадение по случаю (выдержанный аккорд). */
    var MIN_OVERLAP_S = 1.0;
    /* Сколько кандидатов грубого поиска доходят до точной проверки. */
    var CANDIDATES = 4;
    /* Кандидаты ближе этого друг к другу — один и тот же пик. */
    var CAND_SEP_S = 0.25;
    /* Грубый порог: ниже — не кандидат вовсе. */
    var COARSE_MIN = 0.5;
    /* Окончательный порог корреляции по всему нахлёсту. Стенд на 34 песнях
       (части — свои MP3-кодирования, 128–192 кбит/с, 44.1 и 48 кГц): у
       истинных пар не ниже 0.950, у разрывов и чужих песен не выше 0.757. */
    var MATCH_CORR = 0.9;
    /* Тише этого нахлёст не проверить: корреляция тишины — шум. */
    var MIN_RMS_DB = -50;
    /* Окно выбора стыка и доля нахлёста, внутри которой его ищем. */
    var SEAM_WIN_S = 0.02;
    var SEAM_ZONE = [0.2, 0.8];

    function isNum(v) { return typeof v === 'number' && isFinite(v); }

    /* ── ФНЧ: два биквада Баттерворта = 4-й порядок ──────────────────────── */
    function lowpass(fc, fs) {
        var qs = [0.5411961, 1.3065630], out = [];
        for (var i = 0; i < qs.length; i++) {
            var w = 2 * Math.PI * fc / fs, c = Math.cos(w), al = Math.sin(w) / (2 * qs[i]);
            var a0 = 1 + al;
            out.push({
                b0: (1 - c) / 2 / a0, b1: (1 - c) / a0, b2: (1 - c) / 2 / a0,
                a1: -2 * c / a0, a2: (1 - al) / a0
            });
        }
        return out;
    }

    /* Моно → ФНЧ → каждый D-й отсчёт из [start, start + len·D). Фильтр
       прогревается на отсчётах ДО start: отрезок в середине записи должен
       выйти тем же, что вырезан из сплошного фильтрованного сигнала, иначе
       начало края у A и у B отличалось бы переходным процессом. */
    function decimate(channels, start, len, D, sos, warm) {
        var L = channels[0], R = channels.length > 1 ? channels[1] : null;
        var from = Math.max(0, start - warm);
        var out = new Int16Array(len);
        var b0 = sos[0].b0, b1 = sos[0].b1, b2 = sos[0].b2, a1 = sos[0].a1, a2 = sos[0].a2;
        var c0 = sos[1].b0, c1 = sos[1].b1, c2 = sos[1].b2, d1 = sos[1].a1, d2 = sos[1].a2;
        var z1 = 0, z2 = 0, w1 = 0, w2 = 0, x, y, v, i, j, k;
        // Прогрев: тот же фильтр, выход выбрасывается.
        for (i = from; i < start; i++) {
            x = R ? (L[i] + R[i]) * 0.5 : L[i];
            y = b0 * x + z1; z1 = b1 * x - a1 * y + z2; z2 = b2 * x - a2 * y;
            v = c0 * y + w1; w1 = c1 * y - d1 * v + w2; w2 = c2 * y - d2 * v;
        }
        // Каждый D-й отсчёт фильтрованного сигнала, начиная с самого start.
        for (k = 0, i = start; k < len; k++) {
            for (j = 0; j < D; j++, i++) {
                x = R ? (L[i] + R[i]) * 0.5 : L[i];
                y = b0 * x + z1; z1 = b1 * x - a1 * y + z2; z2 = b2 * x - a2 * y;
                v = c0 * y + w1; w1 = c1 * y - d1 * v + w2; w2 = c2 * y - d2 * v;
                if (j === 0) {
                    var q = Math.round(v * 32767);
                    out[k] = q > 32767 ? 32767 : (q < -32768 ? -32768 : q);
                }
            }
        }
        return out;
    }

    /**
     * Края дорожки для match(). channels — массив Float32Array (каналы
     * AudioBuffer), sampleRate — его частота. null, если дорожка слишком
     * коротка, чтобы о нахлёсте вообще говорить.
     */
    function edges(channels, sampleRate, opts) {
        opts = opts || {};
        if (!channels || !channels.length || !channels[0] || !isNum(sampleRate) || sampleRate <= 0) return null;
        var n = channels[0].length;
        for (var c = 1; c < channels.length; c++) if (!channels[c] || channels[c].length < n) n = channels[c] ? channels[c].length : 0;
        var D = Math.max(1, Math.round(sampleRate / EDGE_RATE));
        var rate = sampleRate / D;
        var edgeSec = isNum(opts.edgeSec) ? opts.edgeSec : EDGE_SEC;
        var len = Math.min(Math.floor(edgeSec * rate), Math.floor(n / D));
        if (len < Math.ceil(2 * MIN_OVERLAP_S * rate)) return null;
        var sos = lowpass(0.35 * rate, sampleRate);
        var warm = Math.round(0.05 * sampleRate);
        var tailStart = n - len * D;
        return {
            v: 1,
            srcRate: sampleRate,
            D: D,
            rate: rate,
            n: n,
            dur: n / sampleRate,
            head: decimate(channels, 0, len, D, sos, warm),
            tail: decimate(channels, tailStart, len, D, sos, warm),
            tailStart: tailStart
        };
    }

    /* ── БПФ (radix-2, на месте; поворотные множители — таблицей) ───────── */
    var twCache = {};
    function twiddles(n) {
        if (twCache[n]) return twCache[n];
        var h = n >> 1, c = new Float64Array(h), s = new Float64Array(h);
        for (var k = 0; k < h; k++) { c[k] = Math.cos(2 * Math.PI * k / n); s[k] = Math.sin(2 * Math.PI * k / n); }
        return (twCache[n] = { c: c, s: s });
    }
    function fft(re, im, inverse) {
        var n = re.length, tw = twiddles(n), tc = tw.c, ts = tw.s, sg = inverse ? 1 : -1;
        for (var i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j |= bit;
            if (i < j) {
                var t = re[i]; re[i] = re[j]; re[j] = t;
                t = im[i]; im[i] = im[j]; im[j] = t;
            }
        }
        for (var len = 2; len <= n; len <<= 1) {
            var half = len >> 1, step = n / len;
            for (var s0 = 0; s0 < n; s0 += len) {
                for (var k = 0, w = 0; k < half; k++, w += step) {
                    var wr = tc[w], wi = sg * ts[w];
                    var p = s0 + k, q = p + half;
                    var tr = re[q] * wr - im[q] * wi, ti = re[q] * wi + im[q] * wr;
                    re[q] = re[p] - tr; im[q] = im[p] - ti;
                    re[p] += tr; im[p] += ti;
                }
            }
        }
        if (inverse) for (var m = 0; m < n; m++) { re[m] /= n; im[m] /= n; }
    }

    /* Взаимная корреляция c[lag] = Σ a[lag+m]·b[m] двумя БПФ вместо трёх:
       оба вещественных сигнала едут в одном комплексном (a + i·b), спектры
       разделяются симметрией. */
    function xcorr(a, b, N) {
        var re = new Float64Array(N), im = new Float64Array(N);
        re.set(a); im.set(b);
        fft(re, im, false);
        var pr = new Float64Array(N), pi = new Float64Array(N);
        for (var k = 0; k < N; k++) {
            var j = (N - k) & (N - 1);
            var zr = re[k], zi = im[k], cr = re[j], ci = -im[j];
            var Ar = (zr + cr) * 0.5, Ai = (zi + ci) * 0.5;
            var Br = (zi - ci) * 0.5, Bi = (cr - zr) * 0.5;
            pr[k] = Ar * Br + Ai * Bi;
            pi[k] = Ai * Br - Ar * Bi;
        }
        fft(pr, pi, true);
        return pr;
    }

    /* Край на ~1 кГц для грубого поиска (ФНЧ ещё раз — иначе наложение
       спектров при каждом восьмом отсчёте). Кэшируется на объекте края. */
    function coarseOf(e, which) {
        var key = which === 'head' ? '_coarseHead' : '_coarseTail';
        if (e[key]) return e[key];
        var src = e[which];
        var sos = lowpass(0.35 * e.rate / COARSE_DIV, e.rate);
        var s = sos[0], t = sos[1];
        var len = Math.floor(src.length / COARSE_DIV);
        var out = new Float64Array(len);
        var z1 = 0, z2 = 0, w1 = 0, w2 = 0;
        for (var i = 0; i < len * COARSE_DIV; i++) {
            var x = src[i] / 32768;
            var y = s.b0 * x + z1;
            z1 = s.b1 * x - s.a1 * y + z2;
            z2 = s.b2 * x - s.a2 * y;
            var v = t.b0 * y + w1;
            w1 = t.b1 * y - t.a1 * v + w2;
            w2 = t.b2 * y - t.a2 * v;
            if (i % COARSE_DIV === 0) out[i / COARSE_DIV] = v;
        }
        e[key] = out;
        return out;
    }

    /**
     * Грубые кандидаты: сдвиг lag (в отсчётах грубого края A) — B[0] стоит в
     * точке lag хвоста A, нахлёст = длина хвоста − lag. Оценка — корреляция,
     * нормированная на энергии ИМЕННО ЭТОГО нахлёста (суффикс A × префикс B).
     */
    function coarseCandidates(a, b, minL, maxL) {
        var Na = a.length, Nb = b.length;
        var N = 1;
        while (N < Na + Nb) N <<= 1;
        var ar = xcorr(a, b, N);
        // Суффиксные энергии A и префиксные энергии B.
        var sufA = new Float64Array(Na + 1);
        for (var i = Na - 1; i >= 0; i--) sufA[i] = sufA[i + 1] + a[i] * a[i];
        var preB = new Float64Array(Nb + 1);
        for (var j = 0; j < Nb; j++) preB[j + 1] = preB[j] + b[j] * b[j];
        var score = new Float64Array(Na);
        for (var lag = 0; lag < Na; lag++) {
            var L = Na - lag;
            if (L < minL || L > maxL || L > Nb) { score[lag] = -1; continue; }
            var den = Math.sqrt(sufA[lag] * preB[L]);
            score[lag] = den > 0 ? ar[lag] / den : -1;
        }
        return score;
    }

    function pickPeaks(score, sep, count, floor) {
        var picks = [];
        var used = new Uint8Array(score.length);
        for (var c = 0; c < count; c++) {
            var best = -1, bi = -1;
            for (var i = 0; i < score.length; i++) {
                if (used[i]) continue;
                if (score[i] > best) { best = score[i]; bi = i; }
            }
            if (bi < 0 || best < floor) break;
            picks.push({ lag: bi, score: best });
            for (var k = Math.max(0, bi - sep); k <= Math.min(score.length - 1, bi + sep); k++) used[k] = 1;
        }
        return picks;
    }

    /* Корреляция и энергии хвоста A (с отсчёта lag) и головы B на отрезке
       B[from, to). Возвращает сырые суммы — нормирует вызывающий. */
    function sums(a, b, lag, from, to) {
        var ab = 0, aa = 0, bb = 0;
        for (var m = from; m < to; m++) {
            var x = a[lag + m], y = b[m];
            ab += x * y; aa += x * x; bb += y * y;
        }
        return { ab: ab, aa: aa, bb: bb };
    }

    function ncc(s) { return (s.aa > 0 && s.bb > 0) ? s.ab / Math.sqrt(s.aa * s.bb) : 0; }

    /* Две расшифровки одного файла побитно равны — края совпадают целиком. */
    function sameAudio(ea, eb) {
        if (ea === eb) return true;
        if (ea.n !== eb.n || ea.head.length !== eb.head.length || ea.tail.length !== eb.tail.length) return false;
        for (var i = 0; i < ea.head.length; i++) if (ea.head[i] !== eb.head[i]) return false;
        for (var j = 0; j < ea.tail.length; j++) if (ea.tail[j] !== eb.tail[j]) return false;
        return true;
    }

    /**
     * Где начало B повторяет конец A. Результат в секундах ЗАПИСЕЙ A и B:
     *   offsetSec  — точка A, в которой стоит B[0];
     *   overlapSec — длина повтора (= durA − offsetSec);
     *   seamA / seamB — одна и та же точка музыки в времени A и в времени B:
     *     A звучит до seamA, B — с seamB.
     */
    function match(ea, eb, opts) {
        opts = opts || {};
        if (!ea || !eb || !ea.head || !eb.head) return { found: false, reason: 'no_edges' };
        if (Math.abs(ea.rate - eb.rate) > 1e-6 || ea.D !== eb.D) return { found: false, reason: 'rate' };
        // Один и тот же файл дважды — повтор по замыслу (зациклить, удлинить), а
        // не две части одной записи: у петли из двух одинаковых половин конец
        // равен началу, и «склеить без повтора» сломало бы задуманное.
        if (sameAudio(ea, eb)) return { found: false, reason: 'same' };
        var rate = ea.rate;
        var a = ea.tail, b = eb.head;
        var minOverlap = isNum(opts.minOverlapSec) ? opts.minOverlapSec : MIN_OVERLAP_S;
        var minL = Math.ceil(minOverlap * rate);
        // Нахлёст не может проглотить дорожку целиком: от A должно остаться
        // хоть что-то до стыка, от B — после. Иначе это дубль, а не продолжение.
        var maxL = Math.min(a.length, b.length,
            Math.floor((ea.dur - 0.5) * rate), Math.floor((eb.dur - 0.5) * rate));
        if (maxL < minL) return { found: false, reason: 'short' };

        var ac = coarseOf(ea, 'tail'), bc = coarseOf(eb, 'head');
        var crate = rate / COARSE_DIV;
        var score = coarseCandidates(ac, bc, Math.ceil(minL / COARSE_DIV), Math.floor(maxL / COARSE_DIV));
        var cands = pickPeaks(score, Math.round(CAND_SEP_S * crate), CANDIDATES, COARSE_MIN);
        if (!cands.length) {
            var top = -1;
            for (var q = 0; q < score.length; q++) if (score[q] > top) top = score[q];
            return { found: false, reason: 'no_candidate', coarse: Math.round(top * 1000) / 1000 };
        }

        // Точный сдвиг около каждого кандидата: окно в середине нахлёста.
        var verified = [];
        for (var ci = 0; ci < cands.length; ci++) {
            var lag0 = cands[ci].lag * COARSE_DIV;
            var lo = Math.max(0, lag0 - 3 * COARSE_DIV), hi = Math.min(a.length - minL, lag0 + 3 * COARSE_DIV);
            var bestLag = -1, bestV = -2, vals = {};
            for (var lag = lo; lag <= hi; lag++) {
                var L = a.length - lag;
                if (L > maxL) continue;
                var w = Math.min(L, Math.round(4 * rate));
                var from = Math.floor((L - w) / 2);
                var v = ncc(sums(a, b, lag, from, from + w));
                vals[lag] = v;
                if (v > bestV) { bestV = v; bestLag = lag; }
            }
            if (bestLag < 0) continue;
            // Парабола по трём точкам — дробная часть сдвига.
            var frac = 0;
            var vm = vals[bestLag - 1], vp = vals[bestLag + 1];
            if (isNum(vm) && isNum(vp)) {
                var den = vm - 2 * bestV + vp;
                if (den < 0) frac = Math.max(-0.5, Math.min(0.5, 0.5 * (vm - vp) / den));
            }
            var Lb = a.length - bestLag;
            var full = sums(a, b, bestLag, 0, Lb);
            verified.push({
                lag: bestLag, frac: frac, L: Lb, corr: ncc(full), sums: full,
                coarse: cands[ci].score
            });
        }
        if (!verified.length) return { found: false, reason: 'no_candidate' };
        // Лучший по корреляции всего нахлёста. Если порог проходит ещё и
        // ДРУГОЙ стык — это луп из одних и тех же отсчётов (электроника из
        // DAW): данные не говорят, сколько повторов было в песне, и любой
        // выбор прибавил бы или отнял круг. Тогда честно молчим. У песен Suno
        // второй кандидат на стенде не выше 0.72 — повторы там не побитные.
        verified.sort(function (x, y) { return y.corr - x.corr; });
        var best = verified[0];
        var second = null, rival = false;
        for (var vi = 1; vi < verified.length; vi++) {
            if (Math.abs(verified[vi].lag - best.lag) <= 0.05 * rate) continue;
            if (second === null || verified[vi].corr > second) second = verified[vi].corr;
            if (verified[vi].corr >= MATCH_CORR) rival = true;
        }

        var rmsA = Math.sqrt(best.sums.aa / best.L) / 32768;
        var rmsB = Math.sqrt(best.sums.bb / best.L) / 32768;
        var levelDb = 20 * Math.log10(Math.max(1e-9, Math.min(rmsA, rmsB)));
        var out = {
            corr: Math.round(best.corr * 10000) / 10000,
            coarse: Math.round(best.coarse * 1000) / 1000,
            second: second === null ? null : Math.round(second * 10000) / 10000,
            levelDb: Math.round(levelDb * 10) / 10
        };
        if (levelDb < MIN_RMS_DB) { out.found = false; out.reason = 'silent'; return out; }
        if (best.corr < MATCH_CORR) { out.found = false; out.reason = 'weak'; return out; }
        if (rival) { out.found = false; out.reason = 'ambiguous'; return out; }

        // Усиление B → A по нахлёсту (МНК) и место стыка: в середине нахлёста
        // окно, где A и B согласны лучше всего (обычно — самое тихое место).
        var g = best.sums.bb > 0 ? best.sums.ab / best.sums.bb : 1;
        var win = Math.max(8, Math.round(SEAM_WIN_S * rate));
        var z0 = Math.floor(best.L * SEAM_ZONE[0]), z1 = Math.floor(best.L * SEAM_ZONE[1]) - win;
        var seamIdx = Math.floor(best.L / 2), seamErr = Infinity;
        for (var s0 = z0; s0 <= z1; s0 += Math.max(1, win >> 1)) {
            var err = 0;
            for (var m = s0; m < s0 + win; m++) {
                var d = a[best.lag + m] - g * b[m];
                err += d * d;
            }
            if (err < seamErr) { seamErr = err; seamIdx = s0 + (win >> 1); }
        }

        var lagF = best.lag + best.frac;
        var offsetSec = (ea.tailStart + lagF * ea.D) / ea.srcRate;
        var seamB = seamIdx * eb.D / eb.srcRate;
        out.found = true;
        out.offsetSec = offsetSec;
        out.overlapSec = ea.dur - offsetSec;
        out.seamA = offsetSec + seamB;
        out.seamB = seamB;
        out.gainDb = Math.round(20 * Math.log10(Math.max(1e-6, Math.abs(g))) * 100) / 100;
        return out;
    }

    return {
        edges: edges,
        match: match,
        EDGE_SEC: EDGE_SEC,
        MIN_OVERLAP_S: MIN_OVERLAP_S,
        MATCH_CORR: MATCH_CORR,
        _fft: fft
    };
});
